<script setup>
// whatsapp-automatic-history-sync.RECOVERY.3
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { setupFacebookSdk } from 'dashboard/routes/dashboard/settings/inbox/channels/whatsapp/utils';
import { freshCoexistenceSignup } from 'dashboard/helper/kaptionCoexistenceRecovery';

const props = defineProps({ inboxId: { type: Number, required: true } });
const emit = defineEmits(['updated']);
const { t } = useI18n();
const report = ref(null);
const offboarded = ref(false);
const busy = ref(false);
const error = ref(false);
const success = ref(false);
const ticket = ref(null);

async function review(more = false) {
  busy.value = true;
  error.value = false;
  try {
    const previous = more ? report.value.duplicate_candidates.groups : [];
    const cursor = more ? report.value.duplicate_candidates.next_cursor : 0;
    report.value = (
      await InboxesAPI.getWhatsappRecovery(props.inboxId, cursor)
    ).data;
    report.value.duplicate_candidates.groups.unshift(...previous);
  } catch {
    error.value = true;
  } finally {
    busy.value = false;
  }
}
async function replay() {
  busy.value = true;
  error.value = false;
  try {
    await InboxesAPI.replayWhatsappHistory(props.inboxId);
    await review();
    emit('updated');
  } catch {
    error.value = true;
  } finally {
    busy.value = false;
  }
}
async function prepareSignup() {
  if (!offboarded.value) return;
  busy.value = true;
  error.value = false;
  try {
    const config = window.chatwootConfig;
    if (!config?.whatsappAppId || !config?.whatsappConfigurationId)
      throw Error();
    await setupFacebookSdk(config.whatsappAppId, config.whatsappApiVersion);
    ticket.value = (
      await InboxesAPI.prepareWhatsappRecovery(props.inboxId)
    ).data.ticket;
  } catch {
    error.value = true;
  } finally {
    busy.value = false;
  }
}
async function reconnect() {
  if (!offboarded.value) return;
  busy.value = true;
  error.value = false;
  success.value = false;
  try {
    const signup = await freshCoexistenceSignup(
      window.chatwootConfig.whatsappConfigurationId
    );
    await InboxesAPI.completeWhatsappRecovery(props.inboxId, {
      ...signup,
      ticket: ticket.value,
    });
    success.value = true;
    offboarded.value = false;
    report.value = null;
    emit('updated');
  } catch {
    error.value = true;
  } finally {
    busy.value = false;
    ticket.value = null;
  }
}
</script>

<template>
  <section class="p-4 my-6 border border-n-weak rounded-lg space-y-3">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.TITLE') }}
    </h3>
    <p class="text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.DESCRIPTION') }}
    </p>
    <NextButton :disabled="busy" @click="review(false)">
      {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.REVIEW') }}
    </NextButton>
    <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
      {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.ERROR') }}
    </p>
    <p v-if="success" role="status" class="text-sm text-n-green-11">
      {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.SUCCESS') }}
    </p>
    <template v-if="report">
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.REUSE') }}
      </p>
      <div class="overflow-x-auto">
        <table
          v-if="report.duplicate_candidates.groups.length"
          class="w-full text-sm"
        >
          <thead>
            <tr>
              <th>
                {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.CONTACT') }}
              </th>
              <th>
                {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.CANDIDATES') }}
              </th>
              <th>
                {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.DESTINATION') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="group in report.duplicate_candidates.groups"
              :key="group.contact_id"
            >
              <td>{{ group.contact_id }}</td>
              <td>
                {{
                  group.conversations
                    .map(c => `#${c.id} (${c.messages})`)
                    .join(', ')
                }}
              </td>
              <td>#{{ group.history_destination_id }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.NO_CANDIDATES') }}
        </p>
      </div>
      <p
        v-if="report.duplicate_candidates.truncated"
        class="text-sm text-n-slate-11"
      >
        {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.TRUNCATED') }}
      </p>
      <NextButton
        v-if="report.duplicate_candidates.next_cursor"
        :disabled="busy"
        @click="review(true)"
      >
        {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.MORE') }}
      </NextButton>
      <NextButton
        v-if="report.delivery.failed_deliveries"
        :disabled="busy"
        @click="replay"
      >
        {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.REPLAY') }}
      </NextButton>
      <p
        v-if="
          report.delivery.pending_deliveries ||
          report.delivery.failed_deliveries
        "
        class="text-sm text-n-slate-11"
      >
        {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.WAIT') }}
      </p>
      <template v-else>
        <p class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.OFFBOARD_STEPS') }}
        </p>
        <label class="flex items-start gap-2 text-sm text-n-slate-12">
          <input v-model="offboarded" type="checkbox" :disabled="busy" />
          {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.CONFIRM') }}
        </label>
        <NextButton
          v-if="!ticket"
          :disabled="busy || !offboarded"
          :is-loading="busy"
          @click="prepareSignup"
        >
          {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.PREPARE') }}
        </NextButton>
        <NextButton
          v-else
          :disabled="busy || !offboarded"
          :is-loading="busy"
          @click="reconnect"
        >
          {{ t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.RECOVERY.RECONNECT') }}
        </NextButton>
      </template>
    </template>
  </section>
</template>
